import React, { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import {
  Home, Building2, Waves, Droplets, ThermometerSun, FileCheck2, Hammer,
  ArrowRight, ArrowLeft, Check, Loader2, HardHat, ShieldCheck, HelpCircle,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { DEPARTMENTS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROPERTY = [
  { key: "maison", label: "Maison", icon: Home },
  { key: "appartement", label: "Appartement", icon: Building2 },
  { key: "piscine", label: "Piscine", icon: Droplets },
  { key: "copropriete", label: "Copropriété", icon: Building2 },
  { key: "professionnel", label: "Professionnel", icon: HardHat },
  { key: "assurance", label: "Assurance", icon: ShieldCheck },
];

const PROBLEM = [
  { key: "canalisation", label: "Canalisation encastrée", icon: Waves },
  { key: "piscine", label: "Fuite de piscine", icon: Droplets },
  { key: "chauffage_sol", label: "Chauffage au sol", icon: ThermometerSun },
  { key: "degat_eaux", label: "Dégât des eaux", icon: FileCheck2 },
  { key: "avant_travaux", label: "Avant travaux", icon: Hammer },
  { key: "autre", label: "Autre / je ne sais pas", icon: HelpCircle },
];

const URGENCY = ["Urgent (fuite active)", "Sous 48h", "Pas d'urgence"];

const slide = {
  enter: { opacity: 0, x: 40 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -40 },
};

function SelectCard({ active, onClick, icon: I, label, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`flex flex-col items-start gap-3 rounded-2xl border p-5 text-left transition-colors duration-200 ${
        active
          ? "border-aqua bg-aqua/10"
          : "border-ink/10 bg-white hover:border-aqua/40 hover:bg-mist/60"
      }`}
    >
      <I size={26} strokeWidth={1.5} className={active ? "text-aqua" : "text-ink/70"} />
      <span className={`text-sm font-semibold ${active ? "text-aqua" : "text-ink"}`}>{label}</span>
    </button>
  );
}

export default function QuoteForm({ defaultDepartment = "" }) {
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    property_type: "",
    problem_type: "",
    urgency: "",
    department: defaultDepartment,
    name: "",
    phone: "",
    email: "",
    message: "",
  });

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const canNext =
    (step === 0 && data.property_type) ||
    (step === 1 && data.problem_type) ||
    step === 2;

  const submit = async () => {
    if (!data.name || !data.phone || !data.email) {
      toast.error("Merci de renseigner votre nom, téléphone et email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, data);
      setDone(true);
      toast.success("Demande envoyée ! Nous vous recontactons rapidement.");
    } catch (e) {
      toast.error("Une erreur est survenue. Réessayez ou appelez-nous.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="quote-success" className="rounded-3xl border border-aqua/30 bg-mist/60 p-10 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-aqua text-white">
          <Check size={32} />
        </div>
        <h3 className="mt-6 text-2xl font-bold text-ink">Demande bien reçue !</h3>
        <p className="mt-3 text-ink/70 max-w-md mx-auto">
          Merci {data.name.split(" ")[0]}. Un expert FuiteX vous recontacte au plus vite pour organiser l'intervention.
        </p>
      </div>
    );
  }

  const progress = ((step + 1) / 4) * 100;

  return (
    <div data-testid="quote-form" className="rounded-3xl border border-ink/10 bg-white p-6 sm:p-9 shadow-[0_20px_60px_-30px_rgba(13,27,42,0.4)]">
      <div className="mb-8">
        <div className="flex items-center justify-between text-xs font-bold tracking-wide uppercase text-ink/50 mb-2">
          <span>Étape {step + 1} / 4</span>
          <span className="text-aqua">Devis gratuit</span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-mist overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-aqua"
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={step} variants={slide} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}>
          {step === 0 && (
            <div>
              <h3 className="text-xl font-bold text-ink mb-1">Quel est le type de bien ?</h3>
              <p className="text-sm text-ink/60 mb-6">Sélectionnez la situation la plus proche.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {PROPERTY.map((p) => (
                  <SelectCard
                    key={p.key}
                    testid={`property-${p.key}`}
                    active={data.property_type === p.key}
                    onClick={() => set("property_type", p.key)}
                    icon={p.icon}
                    label={p.label}
                  />
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <h3 className="text-xl font-bold text-ink mb-1">Quel est le problème ?</h3>
              <p className="text-sm text-ink/60 mb-6">Le type de fuite que vous suspectez.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {PROBLEM.map((p) => (
                  <SelectCard
                    key={p.key}
                    testid={`problem-${p.key}`}
                    active={data.problem_type === p.key}
                    onClick={() => set("problem_type", p.key)}
                    icon={p.icon}
                    label={p.label}
                  />
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h3 className="text-xl font-bold text-ink mb-1">Précisez votre situation</h3>
              <p className="text-sm text-ink/60 mb-6">Urgence et localisation.</p>
              <Label className="text-ink font-semibold">Niveau d'urgence</Label>
              <div className="mt-2 flex flex-wrap gap-2.5">
                {URGENCY.map((u) => (
                  <button
                    key={u}
                    type="button"
                    data-testid={`urgency-${u.slice(0, 6)}`}
                    onClick={() => set("urgency", u)}
                    className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                      data.urgency === u ? "border-flame bg-flame text-white" : "border-ink/15 text-ink/70 hover:border-flame/50"
                    }`}
                  >
                    {u}
                  </button>
                ))}
              </div>
              <Label className="text-ink font-semibold mt-6 block">Département</Label>
              <select
                data-testid="department-select"
                value={data.department}
                onChange={(e) => set("department", e.target.value)}
                className="mt-2 w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm text-ink focus:border-aqua focus:outline-none"
              >
                <option value="">Sélectionnez un département</option>
                {DEPARTMENTS.map((d) => (
                  <option key={d.slug} value={`${d.name} (${d.code})`}>{d.name} ({d.code})</option>
                ))}
              </select>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-ink mb-1">Vos coordonnées</h3>
              <p className="text-sm text-ink/60 mb-4">Nous vous recontactons rapidement.</p>
              <div>
                <Label htmlFor="q-name" className="text-ink font-semibold">Nom complet *</Label>
                <Input id="q-name" data-testid="quote-name" value={data.name} onChange={(e) => set("name", e.target.value)} placeholder="Jean Dupont" className="mt-1.5" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="q-phone" className="text-ink font-semibold">Téléphone *</Label>
                  <Input id="q-phone" data-testid="quote-phone" value={data.phone} onChange={(e) => set("phone", e.target.value)} placeholder="06 12 34 56 78" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="q-email" className="text-ink font-semibold">Email *</Label>
                  <Input id="q-email" data-testid="quote-email" type="email" value={data.email} onChange={(e) => set("email", e.target.value)} placeholder="jean@email.fr" className="mt-1.5" />
                </div>
              </div>
              <div>
                <Label htmlFor="q-msg" className="text-ink font-semibold">Message (facultatif)</Label>
                <Textarea id="q-msg" data-testid="quote-message" value={data.message} onChange={(e) => set("message", e.target.value)} placeholder="Décrivez votre problème…" className="mt-1.5" rows={3} />
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="mt-8 flex items-center justify-between">
        {step > 0 ? (
          <button data-testid="quote-back" onClick={() => setStep((s) => s - 1)} className="inline-flex items-center gap-2 text-sm font-semibold text-ink/60 hover:text-ink transition-colors">
            <ArrowLeft size={16} /> Retour
          </button>
        ) : <span />}

        {step < 3 ? (
          <button
            data-testid="quote-next"
            disabled={!canNext}
            onClick={() => setStep((s) => s + 1)}
            className="btn-cta disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Continuer <ArrowRight size={16} />
          </button>
        ) : (
          <button data-testid="quote-submit" disabled={loading} onClick={submit} className="btn-cta disabled:opacity-60">
            {loading ? <><Loader2 size={16} className="animate-spin" /> Envoi…</> : <>Envoyer ma demande <Check size={16} /></>}
          </button>
        )}
      </div>
    </div>
  );
}
